import * as React from "react";

import "VSS/LoaderPlugins/Css!Agile/Scripts/Common/Components/PreloadedReportComponent";
import { LWPComponent } from "VSSPreview/Flux/Components/LWP";
import { getService } from "VSS/Service";
import { FeatureManagementService } from "VSS/FeatureManagement/Services";
import { ISprintViewPivotContext } from "Agile/Scripts/SprintsHub/SprintView/SprintContentViewContracts";
import { getBurndownProps } from "Agile/Scripts/Common/Components/BurndownReportPropsHelper";
import { getLWPService} from "VSS/LWP";
import { KeyCode } from "VSS/Utils/UI";
import { WorkItemChangeType } from "WorkItemTracking/Scripts/OM/WorkItemConstants";
import { ProjectCollection } from "Presentation/Scripts/TFS/TFS.OM.Common";
import { WorkItemManager } from "WorkItemTracking/Scripts/OM/WorkItemManager";
import { WorkItemStore } from "WorkItemTracking/Scripts/TFS.WorkItemTracking";
import { TfsContext } from "Presentation/Scripts/TFS/TFS.Host.TfsContext";
import Utils_Core = require("VSS/Utils/Core");

var delegate = Utils_Core.delegate;

interface PreloadedReportComponentProps {

    /** Name of hub where hub tile is loaded . Used to decide which report's scripts need to be loaded */
    agileHub: AgileHub;

    /** Pivot context used for preloading of reports with data . Can be a switching type of BacklogsPivotContext and BoardsPivotContext in future when we decide to preload data on those pages */
    pivotContext?: ISprintViewPivotContext;

    /** Current pivot name */
    currentPivot?: string;
}


interface PreloadedReportComponentState {

    preloadReports: boolean;
    loadAxHeaderChart: boolean;
}

export enum AgileHub {
    Sprint,
    Backlog,
    Board
}

export class PreloadedReportComponent extends React.Component<PreloadedReportComponentProps, PreloadedReportComponentState> {

    private workItemChangedDelegate: IEventHandler;
    private timerHandle: number;
    public delayHeaderChartUpdate = 180000;

    constructor(props: PreloadedReportComponentProps) {
        super(props);
        this.state = {preloadReports : false , loadAxHeaderChart : false};
        this.workItemChangedDelegate = delegate(this, this.workItemChanged);
    }

    public componentDidMount() {
        this.attachEvents();
        this.setChartRenderingState();
    }

    public componentDidUpdate(prevProps : PreloadedReportComponentProps) {
        if (JSON.stringify(this.props.currentPivot) !== JSON.stringify(prevProps.currentPivot)) {
            this.setChartRenderingState();
        }
    }

    public componentWillUnmount() {
        this.detachEvents();
    }

    private workItemChanged(sender, args) {
        if (args.change === WorkItemChangeType.SaveCompleted) {
          
            if (this.timerHandle) {
                clearTimeout(this.timerHandle);
            }

            // Delay chart update by 10 seconds to account for AX latency
            // TODO extend this to do the update after data quality with AX is checked
            this.timerHandle = setTimeout(() => {
                this.setChartRenderingState();
            }, this.delayHeaderChartUpdate);
        }
    }

    /**
     * Attaches event handler that reacts to external event like changes in work items on task board so we can reload header chart
     */
    public attachEvents() {
        this.getWorkItemManager().attachWorkItemChanged(this.workItemChangedDelegate);
    }

    /**
     * Detaches event handlers that react to external event like changes in work items on task board so we can reload header chart
     */
    public detachEvents() {
        this.getWorkItemManager().detachWorkItemChanged(this.workItemChangedDelegate);
    }

    private getWorkItemManager(): WorkItemManager {
        const tfsContext = TfsContext.getDefault();
        const store = ProjectCollection.getConnection(tfsContext).getService<WorkItemStore>(WorkItemStore);
        return WorkItemManager.get(store);
    }

    public render(): JSX.Element {

        let reportContentPreloaded = null;
        const {preloadReports , loadAxHeaderChart } = this.state;
        if(preloadReports) 
        {
            reportContentPreloaded = this.props.agileHub === AgileHub.Sprint ? this.renderBurndownReport() : this.renderCFDVelocityReports();
        }
        
        //  Display chart and lightbox only if both preloading and show header chart features are on and only in Sprints Hub.
        if(loadAxHeaderChart && preloadReports && this.props.agileHub === AgileHub.Sprint) {

            return (
                <>
                    <div className={"ax-header-chart clickable small-chart-container"} role="button" onClick={()=>this.showLightBox(this.props.pivotContext)} onKeyDown={this.onKeyDown} >
                        {reportContentPreloaded}
                    </div>
                </>
            );
        }
        else
        {
            //  When both axheaderChart and preload feature flags are off , this will put a display none div around null component
            //  When axheaderChart is on and preload feature flag is off , this will put a display none div around null component
            //  When axheaderChart is off and preload feature flags is on , this will put a display none div around fully preloaded report component
            //  When not in Sprints Hub, this will put a display none div around fully preloaded CFD Velocity reports preloaders.
            return (
                <>
                    <div style={{ display: 'none' }}>
                        {reportContentPreloaded}
                    </div>
                </>
            );
        }
    }

    private setChartRenderingState = () => {
        const featureManagementService = getService(FeatureManagementService);
        const preloadReports = featureManagementService.isFeatureEnabled("ms.vss-reporting-web.reporting-boards-reports-preload");
        const isAxHeaderChartEnabled = featureManagementService.isFeatureEnabled("ms.vss-reporting-web.reporting-boards-ax-header-chart");
        const isAnalyticsPivot = this.props.pivotContext && this.props.pivotContext.pivotName === "analytics";
        const loadAxHeaderChart = isAxHeaderChartEnabled && !isAnalyticsPivot; // do not load ax header chart on AX pivot
        this.setState({preloadReports : preloadReports , loadAxHeaderChart : loadAxHeaderChart});
    };

    private showLightBox(pivotContext: ISprintViewPivotContext): void {

        const layoutManager = getLWPService("IVssLayoutManager");
        layoutManager.renderCallout((onDismiss) => {
            const lightboxProps={
                title:"",
                onDismiss: onDismiss,
                className:"lightbox-report-content"
            };

            const reportSettings = getBurndownProps(pivotContext);
            reportSettings.isLightbox = true;
    
            return <LWPComponent
                wrappedType={"Lightbox"}
                dependencies={["ms.vss-reporting.reporting-lightbox"]}
            {...lightboxProps} >
                <LWPComponent
                    wrappedType={"BurndownReport"}
                    dependencies={["ms.vss-reporting-web.burndown-report-content"]}
                    {...reportSettings} ></LWPComponent>
            </LWPComponent>
        });
    }

    private onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
            if (event.keyCode === KeyCode.SPACE || event.keyCode === KeyCode.ENTER) {
                this.showLightBox(this.props.pivotContext);
            }
    }

    private renderBurndownReport(): JSX.Element {

        const reportSettings = getBurndownProps(this.props.pivotContext);
        reportSettings.isMinimalChart = true;
        reportSettings.lastRefresh = new Date();
        return <LWPComponent
            key={"burndown-report"}
            wrappedType={"BurndownReport"}
            dependencies={["ms.vss-reporting-web.burndown-report-content"]}
            {...reportSettings}
        />;
    }

    private renderCFDVelocityReports(): JSX.Element {
        return <>
            {this.renderReportSelector("")}
            {this.renderVelocityReport("")}
            {this.renderCFDReport("")}
        </>;
    }

    private renderReportSelector(reportSettings): JSX.Element {
        return <LWPComponent
            wrappedType={"BoardsReportSelectorPreloader"}
            dependencies={["ms.vss-reporting-web.boards-report-view-content"]}
            {...reportSettings} />
    }

    private renderVelocityReport(reportSettings): JSX.Element {
        return <LWPComponent
            wrappedType={"VelocityReportPreloader"}
            dependencies={["ms.vss-reporting-web.velocity-report-content"]}
            {...reportSettings} />
    }

    private renderCFDReport(reportSettings): JSX.Element {
        return <LWPComponent
            wrappedType={"CFDReportPreloader"}
            dependencies={["ms.vss-reporting-web.cfd-report-content"]}
            {...reportSettings} />;
    }
}